"use client";

import Image from "next/image";
import { useRef, useState, type CSSProperties } from "react";
import clsx from "clsx";
import ScrollReveal from "../common/ScrollReveal";
import { SelectCell } from "../common/SelectCell";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import {
  ACCORDION_PANEL_BG,
  AccordionChevron,
  accordionBodyClosed,
  accordionBodyOpen,
  accordionCard,
  accordionDivider,
  accordionHeader,
  accordionPanel,
  accordionPanelClip,
  accordionPanelClosed,
  accordionPanelOpen,
} from "../common/accordion";

type FeatureKey = "TRAINING" | "CHALLENGES" | "SCENARIO" | "ORCHESTRATION";

/**
 * A bullet is either a plain line, or a capability named in bold followed by
 * what it actually does for the reader. The revision brief introduced the second
 * shape for the Training and Orchestration tabs, where every feature now carries
 * a line of value rather than standing alone as a noun.
 */
type Bullet = string | { term: string; text: string };

interface FeatureContent {
  label: string;
  image: string;
  description?: string;
  bullets: Bullet[];
  /** Render bullets in a two-column grid (Figma "Training Management" panel). */
  twoColumnBullets?: boolean;
}

/* Copy follows "HackGrid Cyber Range — Website Revision (1)" verbatim. Two spots
   in that brief read as if a connecting word was dropped in editing — they are
   marked below and kept as written rather than silently patched. */
const FEATURES: Record<FeatureKey, FeatureContent> = {
  TRAINING: {
    label: "Training Management System",
    image: "/images/Product/training_management_system.svg",
    description:
      "HackGrid lets instructors and participants interact with the underlying components of the Cyber Range through a single, user-friendly interface. Key capabilities:",
    bullets: [
      {
        term: "Range & scenario management",
        text: "provision and control training environments end to end",
      },
      {
        term: "Live Battle (Red vs Blue)",
        text: "run real-time attack–defense exercises with automated scoring and a live scoreboard",
      },
      {
        term: "CTF & team competitions",
        text: "flag-based challenges in solo or team mode, with live leaderboards",
      },
      {
        term: "Team management",
        text: "organize participants, roles, and permissions",
      },
      {
        term: "Live chat & monitoring",
        text: "communicate and observe teams as exercises unfold",
      },
      {
        term: "Automated scoring & reporting",
        text: "auto-grade submissions (with instructor override) and export analytics as PDF",
      },
      {
        term: "AI-assisted design",
        text: "generates network diagrams and scenarios with the built-in AI assistant",
      },
      {
        term: "Video recording & session replay",
        text: "capture sessions for review, replay, and assessment",
      },
    ],
    // The Figma panel put four bare nouns in two columns. Eight capabilities that
    // each carry a line of value do not survive that: measured against the panel,
    // two columns overflow by 184px at 1440 but 272px at 1280, where the narrower
    // column wraps every line, while one column is 216px / 220px — flatter, and
    // far easier to read a term-plus-description list in.
  },
  CHALLENGES: {
    label: "Comprehensive Challenges",
    image: "/images/Product/comprehensive_challenges.svg",
    description:
      "A vast, continually expanding challenge library that keeps every skill level engaged:",
    bullets: [
      "600+ CTF challenges across diverse categories",
      "300+ coding challenges in 30+ programming languages",
      "150+ labs in fundamental security",
      "450+ malware families across multiple threat categories",
      "300+ vulnerabilities covering real-world attack scenarios",
      "46 hours + 100 labs for Web 101 by CyberJutsu",
      "70 hours + 40 labs for Red Team 101 by CyberJutsu × ECQ",
      "OT/ICS security training and hacking labs",
    ],
  },
  SCENARIO: {
    label: "Scenario & Attack Repo",
    image: "/images/Product/scenario_and_attack_repo.svg",
    description:
      "A ready-to-use repository of scenarios, vulnerabilities, and live malware for hands-on training:",
    bullets: [
      "Customizable scenarios and network diagrams",
      "Hundreds of vulnerabilities ready for instant deployment",
      // brief reads "ready to deploy train and test" — its own change list only
      // called for "ready for deploy" → "ready to deploy", so the "to" before
      // "train" looks dropped by accident. Left as the brief has it.
      "Tens of thousands of live malware samples ready to deploy train and test blue teams in forensics, threat hunting, and incident response",
    ],
  },
  ORCHESTRATION: {
    label: "Powerful Orchestration",
    image: "/images/Product/powerful_orchestration.svg",
    description:
      "HackGrid enables dynamic network-topology creation and host provisioning through drag-and-drop image libraries and templates. It offers four types of orchestration:",
    bullets: [
      // the other three read on from their bold term; this one lost the "with"
      // the previous copy had, and the brief does not list that as a change.
      {
        term: "Physical emulation",
        text: "real PLC, OT, and IoT devices that simulate operations found in critical-infrastructure sectors such as oil refineries, power plants, and healthcare",
      },
      { term: "Virtualization", text: "of machines and network devices" },
      {
        term: "Containerization",
        text: "to rapidly deploy vulnerabilities or applications",
      },
      {
        term: "AWS/GCP cloud integration",
        text: "to simulate environments that closely resemble real production setups",
      },
    ],
  },
};

const FEATURE_ORDER: FeatureKey[] = [
  "TRAINING",
  "CHALLENGES",
  "SCENARIO",
  "ORCHESTRATION",
];

// Slides shown inside the single framed panel. Index 0 is the wheel ("first
// image"); indexes 1–4 are the four features, in wheel/tab order.
const SLIDE_COUNT = FEATURE_ORDER.length + 1;

/**
 * Clickable wheel segments (slide 0). Positions map to the four outer triangles
 * of the HackGrid core; Red/Blue Team in the centre are decorative. `slide` is
 * the slider index the segment jumps to.
 */
const WHEEL_SEGMENTS: { label: string; slide: number; pos: string }[] = [
  { label: "Training Management Systems", slide: 1, pos: "top-[13%] left-[13%]" },
  { label: "Comprehensive Challenges", slide: 2, pos: "top-[13%] right-[13%]" },
  { label: "Powerful Orchestration", slide: 4, pos: "bottom-[13%] left-[13%]" },
  { label: "Flexible Scenario", slide: 3, pos: "bottom-[13%] right-[13%]" },
];

/**
 * The feature's content itself — illustration, lead-in, bullets — with no
 * opinion about the box around it. It is rendered twice: inside the desktop
 * slider frame ({@link FeatureSlide}) and, below `lg`, inside the accordion
 * panel that opens directly under the feature's own tab.
 */
function FeatureBody({ feature }: { feature: FeatureContent }) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex w-full shrink-0 items-center justify-center">
        <Image
          src={feature.image}
          alt={feature.label}
          width={1200}
          height={420}
          className="max-h-[220px] w-auto max-w-full object-contain"
        />
      </div>
      <div className="flex flex-col gap-3">
        {feature.description && (
          <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
            {feature.description}
          </p>
        )}
        <ul
          className={
            feature.twoColumnBullets
              ? "grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2"
              : "flex flex-col gap-2"
          }
        >
          {feature.bullets.map((bullet, i) => (
            <li key={i} className="flex items-start gap-4">
              <span
                aria-hidden
                className="shrink-0 mt-[10px] block h-2 w-2 rounded-full bg-primary"
              />
              <p className="flex-1 min-w-0 text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                {typeof bullet === "string" ? (
                  bullet
                ) : (
                  <>
                    <span className="font-semibold">{bullet.term}</span>{" "}
                    {bullet.text}
                  </>
                )}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FeatureSlide({ feature }: { feature: FeatureContent }) {
  // min-h-full + my-auto centers the content when it's shorter than the panel,
  // but (unlike justify-center) still lets a tall slide scroll all the way to
  // the top and bottom instead of clipping the overflow.
  return (
    <div className="flex min-h-full flex-col pt-8 pb-14">
      <div className="my-auto">
        <FeatureBody feature={feature} />
      </div>
    </div>
  );
}

export default function HackgridProduct() {
  // 0 = the wheel ("first image"); 1–4 = the four feature slides. Below `lg` the
  // same number says which accordion row is open (0 = all closed), so one piece
  // of state drives both layouts.
  const [active, setActive] = useState(0);
  const isDesktop = useIsDesktop();
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const go = (i: number) => setActive((i + SLIDE_COUNT) % SLIDE_COUNT);

  /**
   * Below `lg` the content opens inside the tab list, so a row far down the list
   * would still push its panel below the fold. Park the row just under the fixed
   * 72px header and the whole panel lands in the viewport without the reader
   * scrolling. Desktop keeps the plain slide change.
   */
  const select = (i: number) => {
    const previous = active;
    go(i);
    if (isDesktop || i === 0) return;
    const row = rowRefs.current[i - 1];
    if (!row) return;
    // The row a tap opens is only pushed around by what closes ABOVE it: that
    // panel takes 380ms to collapse, so measuring after the tap would aim at a
    // position the page is about to leave. Take the final position up front —
    // where the row is now, minus the height the panel above is about to give up.
    const collapsingAbove =
      previous > 0 && previous < i
        ? (panelRefs.current[previous - 1]?.getBoundingClientRect().height ?? 0)
        : 0;
    const top = Math.max(
      0,
      row.getBoundingClientRect().top + window.scrollY - 88 - collapsingAbove,
    );
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    requestAnimationFrame(() =>
      window.scrollTo({ top, behavior: smooth ? "smooth" : "auto" }),
    );
  };

  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pt-2 pb-16 overflow-hidden">
      <div className="max-w-[1300px] mx-auto">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[5fr_6fr] lg:gap-10 xl:grid-cols-[550px_1fr] xl:gap-[75px] items-start lg:items-stretch">
          {/* Left: intro + tab list. On lg+ this column's natural height (intro
              + four header-size h-16 tabs) drives the row height and the right
              panel shrinks to match it — so both columns are fully filled and end
              level, with the tabs kept at the exact header cell size. */}
          <div className="flex flex-col gap-6">
            {/* One blank line between paragraphs, the site-wide body rhythm
                (see OTSecurity.tsx). The gap lives here rather than on the <p>
                because each paragraph is wrapped in its own ScrollReveal. */}
            <div className="flex flex-col gap-6 sm:gap-7">
              <ScrollReveal direction="up" delay={0}>
                <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                  <span className="font-semibold text-primary">
                    HackGrid Cyber Range
                  </span>{" "}
                  redefines cybersecurity training through realistic simulation
                  and emulation. It helps individuals and organizations sharpen
                  practical skills, validate expertise, and tackle real-world
                  challenges with confidence.
                </p>
              </ScrollReveal>

              <ScrollReveal direction="up" delay={100}>
                <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                  Built around personalized learning pathways, HackGrid makes
                  training effective, efficient, and tailored to each individual
                  and organization. At its core, the platform brings together
                  everything a modern Cyber Range needs: team management,
                  scenario and attack-content development, realistic
                  user-activity and attack-traffic simulation, live traffic
                  monitoring and analysis, and a full set of instructor tools to
                  communicate, replay, and interact with teams in real time.
                </p>
              </ScrollReveal>
            </div>

            <ScrollReveal direction="up" delay={200}>
              {/* The feature list wears a different skin at each breakpoint.

                  From `lg` it is the Figma desktop layout: the header's shared
                  "Select menu" cell at the same fixed h-16 — red active text + red
                  left border, gradient resting background, no arrow — laid out as
                  a loose stack that drives the slider on the right. Clicking the
                  active tab again returns to the wheel overview (slide 0).

                  Below `lg` it is the site's shared mobile accordion instead, byte
                  for byte the one SHRED, Scarf, Local Regulatory and OT Security
                  use: a single #171A1D card with hairline separators, 56px rows,
                  and the open row marked by red text and a flipped chevron alone.
                  The 5px red left edge does NOT come along — sub-`lg` that mark
                  belongs to page titles (every layout's h1 uses it), and the cell
                  version only ever framed the 64px header while leaving the ~430px
                  panel beneath it bare.

                  Only one of the two headers is ever rendered: the other is
                  `display:none`, so it is out of the accessibility tree and out of
                  the tab order too, and no state has to be duplicated. */}
              <div
                className={clsx(
                  accordionCard,
                  "flex flex-col",
                  "lg:gap-2 lg:overflow-visible lg:rounded-none lg:bg-transparent",
                )}
              >
                {FEATURE_ORDER.map((key, i) => {
                  const slideIndex = i + 1;
                  const isActive = active === slideIndex;
                  const panelId = `hg-feature-${key.toLowerCase()}`;
                  const toggle = () => select(isActive ? 0 : slideIndex);
                  return (
                    // Pairs a header with its panel and carries the row ref the
                    // scroll parking in `select` measures.
                    <div
                      key={key}
                      ref={(el) => {
                        rowRefs.current[i] = el;
                      }}
                      className="flex flex-col"
                    >
                      {/* Accordion row — the sub-`lg` header. */}
                      <button
                        type="button"
                        aria-expanded={isActive}
                        aria-controls={panelId}
                        onClick={toggle}
                        className={clsx(
                          accordionHeader,
                          "lg:hidden",
                          i > 0 && accordionDivider,
                          // `accordionHeader` sets the resting weight (600); the
                          // open row goes to Bold so the selection reads at a
                          // glance, matching the `lg`+ cell below. `!` because
                          // Tailwind emits font-weight utilities alphabetically,
                          // so `font-semibold` would otherwise outrank a plain
                          // `font-bold` at equal specificity.
                          isActive
                            ? "font-bold! text-primary"
                            : "text-text-default hover:text-text-highlight",
                        )}
                      >
                        <span className="flex-1 min-w-0">
                          {FEATURES[key].label}
                        </span>
                        <AccordionChevron open={isActive} />
                      </button>

                      {/* Select-menu cell — the `lg`+ header. */}
                      <div className="hidden lg:block">
                        <SelectCell
                          label={FEATURES[key].label}
                          active={isActive}
                          onClick={toggle}
                          showArrow={false}
                          alwaysGradient
                          boldWhenActive
                        />
                      </div>

                      <div
                        id={panelId}
                        ref={(el) => {
                          panelRefs.current[i] = el;
                        }}
                        role="region"
                        aria-label={FEATURES[key].label}
                        inert={!isActive}
                        className={clsx(
                          "lg:hidden",
                          accordionPanel,
                          isActive ? accordionPanelOpen : accordionPanelClosed,
                        )}
                        style={{ background: ACCORDION_PANEL_BG }}
                      >
                        <div className={accordionPanelClip}>
                          <div
                            className={clsx(
                              // Same panel padding as every other accordion on the
                              // site (`accordionBody`); the typography itself comes
                              // from FeatureBody, which desktop shares.
                              "px-4 pt-2 pb-6 sm:px-5 sm:pb-8",
                              "transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
                              isActive ? accordionBodyOpen : accordionBodyClosed,
                            )}
                          >
                            <FeatureBody feature={FEATURES[key]} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollReveal>
          </div>

          {/* Right: a single framed panel that acts as a 5-slide slider
              (wheel + 4 features) on `lg`+. min-w-0 lets the 1fr grid column
              ignore the slider track's (overflowing) content width so the frame
              stays at its cell width instead of stretching.

              Below `lg` the features have moved into the tab accordion, so this is
              the wheel alone: it leads the section (order-first, like the other
              product pages' hero visual), takes its height from the wheel, and
              drops the frame entirely — border, gradient, texture and shadow are
              `lg:` only, so the wheel sits on the page like the illustrations
              inside the accordion panels do. */}
          <ScrollReveal
            direction="right"
            delay={300}
            className="min-w-0 order-first lg:order-none"
          >
            <div className="relative w-full min-w-0 overflow-hidden flex flex-col lg:h-[672px] lg:rounded-[16px] lg:border lg:border-[#3F444A] lg:bg-[linear-gradient(180deg,#111113_73%,#1d1e20_104%)] lg:drop-shadow-[-16px_21px_14px_rgba(0,0,0,0.3)]">
              {/* Slide track. The slide offset only applies from `lg` (see the
                  .hg-track rule): below that the track holds the wheel alone. */}
              <div
                className="hg-track flex lg:h-full min-w-0 transition-transform duration-500 ease-out"
                style={{ "--hg-shift": `${active * 100}%` } as CSSProperties}
              >
                {/* Slide 0 — the wheel */}
                <div className="relative w-full shrink-0 flex items-center justify-center lg:h-full lg:px-10 lg:py-8">
                  <Image
                    src="/images/Product/hackgrid_panel_texture.png"
                    alt=""
                    fill
                    aria-hidden
                    sizes="(max-width: 1024px) 90vw, 675px"
                    className="hidden lg:block absolute inset-0 object-cover object-bottom opacity-35 pointer-events-none select-none"
                  />
                  <div className="relative w-full max-w-[420px] aspect-square">
                    {/* Outer ring rotates continuously (Figma "HackGrid Round"). */}
                    <div className="hg-spin absolute inset-0 pointer-events-none">
                      <Image
                        src="/images/Home/Hackgrid_circle.svg"
                        alt=""
                        fill
                        sizes="(max-width: 1024px) 80vw, 420px"
                        className="object-contain opacity-90"
                        aria-hidden
                        priority
                      />
                    </div>
                    {/* Static center — Red Team vs Blue Team. */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative h-[78%] w-[78%]">
                        <Image
                          src="/images/Home/Hackgrid_core.svg"
                          alt="HackGrid Cyber Range — Red Team vs Blue Team wheel"
                          fill
                          sizes="(max-width: 1024px) 62vw, 328px"
                          className="object-contain"
                          priority
                        />
                      </div>
                    </div>

                    {/* Clickable segment hotspots over the four outer triangles.
                        Below `lg` this opens the matching accordion row and
                        scrolls to it. */}
                    {WHEEL_SEGMENTS.map((seg) => (
                      <button
                        key={seg.label}
                        type="button"
                        onClick={() => select(seg.slide)}
                        aria-label={`View ${seg.label}`}
                        title={seg.label}
                        className={`absolute ${seg.pos} h-[30%] w-[34%] cursor-pointer bg-transparent focus:outline-none`}
                      />
                    ))}
                  </div>
                </div>

                {/* Slides 1–4 — features (scroll inside the fixed-height frame
                    if a slide's content is taller than the panel). Only exist as
                    slides from `lg`; below that the accordion owns this content,
                    and keeping them here would read it out twice. */}
                {FEATURE_ORDER.map((key) => (
                  <div
                    key={key}
                    className="hg-slide hidden lg:block w-full shrink-0 h-full overflow-y-auto px-6 sm:px-10 lg:px-12"
                  >
                    <FeatureSlide feature={FEATURES[key]} />
                  </div>
                ))}
              </div>

              {/* Dots — first dot returns to the wheel ("first image"). Hidden
                  below `lg`, where the wheel is the frame's only slide. */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 hidden lg:flex items-center gap-3">
                {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => go(i)}
                    aria-label={i === 0 ? "Back to overview" : `Go to slide ${i}`}
                    aria-current={active === i}
                    className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                      active === i
                        ? "w-6 bg-primary"
                        : "w-2 bg-neutral-500 hover:bg-text-highlight"
                    }`}
                  />
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>

      <style jsx>{`
        /* Only the wheel lives in the frame below lg, so the slide offset (fed in
           as --hg-shift) applies from lg up. */
        .hg-track {
          transform: none;
        }
        @media (min-width: 1024px) {
          .hg-track {
            transform: translateX(calc(-1 * var(--hg-shift, 0%)));
          }
        }
        .hg-slide {
          scrollbar-width: none;
        }
        .hg-slide::-webkit-scrollbar {
          display: none;
        }
        .hg-spin {
          animation: hgSpin 40s linear infinite;
          will-change: transform;
        }
        @keyframes hgSpin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hg-spin {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
